import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useInsuranceStore } from 'src/store/insurance';
import { useApplicationStore } from 'src/store/application';
import { CignaCard } from 'src/features/profile/components/Id-cards/cigna-card';
import { EquitableCard } from 'src/features/profile/components/Id-cards/equitable-card';
import { GuardianCard } from 'src/features/profile/components/Id-cards/guardian-card';
import { TelehealthCard } from 'src/features/profile/components/Id-cards/telehealth-card';
import { LegalShieldCard } from 'src/features/profile/components/Id-cards/legalshield-card';
import { VisionCard } from 'src/features/profile/components/Id-cards/vision-card';
import { DentalCard } from 'src/features/profile/components/Id-cards/dental-card';
// import { membersService } from 'src/services/members.service';
import { getSubscriptionPlans } from 'src/services/billing';
import { useIDCardDownload } from 'src/hooks/useIDCardDownload';
import { IdCardType } from 'src/services/interfaces/insurance';

const getApplicantFirstName = (applicant?: any) =>
  applicant?.firstName || applicant?.data?.firstName || '';

const getApplicantMiddleName = (applicant?: any) =>
  applicant?.midName || applicant?.data?.midName || '';

const getApplicantLastName = (applicant?: any) =>
  applicant?.lastName || applicant?.data?.lastName || '';

const getApplicantDob = (applicant?: any) =>
  applicant?.dob || applicant?.data?.dob || '';

const getApplicantFullName = (applicant?: any) =>
  [
    getApplicantFirstName(applicant),
    getApplicantMiddleName(applicant),
    getApplicantLastName(applicant),
  ]
    .filter(Boolean)
    .join(' ');

const formatApplicantCode = (index: number) =>
  String(Math.max(index + 1, 1)).padStart(2, '0');

const matchApplicant = (left?: any, right?: any) => {
  if (!left || !right) return false;
  const leftDob = getApplicantDob(left);
  const rightDob = getApplicantDob(right);

  const namesMatch =
    getApplicantFirstName(left) === getApplicantFirstName(right) &&
    getApplicantLastName(left) === getApplicantLastName(right);

  if (!namesMatch) return false;
  if (!leftDob || !rightDob) return true;

  return leftDob === rightDob;
};

export const IDCardView: React.FC = () => {
  const { subscriptionId, applicantId } = useParams();
  const navigate = useNavigate();
  const { plans: allPlans } = useInsuranceStore();
  const { applications } = useApplicationStore();
  const [cardSide, setCardSide] = useState<'front' | 'back'>('front');
  const cardRef = useRef<HTMLDivElement>(null);
  const [planId, setPlanId] = useState<number | null>(null);
  const [applicant, setApplicant] = useState<any>(null);

  useEffect(() => {
    getSubscriptionPlans().then((plans) => {
      const plan = plans.find((p: any) => p.id === Number(subscriptionId));
      if (plan?.planId) {
        setPlanId(plan?.planId);
        setApplicant(plan?.applicants.find((a: any) => a.id === Number(applicantId)));
      }
    });
  }, [subscriptionId, applicantId])

  const plan = useMemo(() => {
    const idNum = Number(planId);
    if (Number.isNaN(idNum)) return undefined;
    return allPlans.find((p: any) => p.id === idNum)
  }, [planId, allPlans]);

  const cignaCardIdentity = useMemo(() => {
    const applicationApplicants = applications[0]?.applicants || [];
    const primaryApplicant =
      applicationApplicants.find((app: any) => Number(app.applicant) === 1) ||
      applicationApplicants[0];
    const memberApplicant =
      applicationApplicants.find((app: any) => matchApplicant(app, applicant)) ||
      applicant ||
      primaryApplicant;
    const memberIndex = memberApplicant
      ? applicationApplicants.findIndex((app: any) => matchApplicant(app, memberApplicant))
      : -1;
    const memberName =
      getApplicantFullName(memberApplicant) ||
      getApplicantFullName(primaryApplicant) ||
      '{FirstName LastName}';

    return {
      subscriberName: getApplicantFullName(primaryApplicant) || memberName,
      memberName,
      memberCode: formatApplicantCode(memberIndex >= 0 ? memberIndex : 0),
    };
  }, [applications, applicant]);

  const hasBackSide = useMemo(() => {
    const name = (plan?.name || plan?.marketingName || '').toLowerCase();
    return plan?.idCard === IdCardType.CignaMedical
      || plan?.idCard === IdCardType.EquitableDental
      || name.includes('cigna')
      || name.includes('equitable');
  }, [plan]);

  const renderCardForPlan = (side?: 'front' | 'back') => {
    const currentSide = side || cardSide;
    const name = (plan?.name || plan?.marketingName || '').toLowerCase();
    const category = (plan?.category || '').toLowerCase();
    if (plan?.idCard === IdCardType.CignaMedical || name.includes('cigna')) {
      const rxCostsValue = (applicant as any)?.rxCosts || (applicant?.data as any)?.rxCosts;
      return (
        <CignaCard
          side={currentSide}
          plan={plan}
          memberId={applicant?.memberId}
          groupId={applicant?.groupId}
          rxGroupId={applicant?.rxGroupId}
          rxCosts={rxCostsValue}
          subscriberName={cignaCardIdentity.subscriberName}
          memberName={cignaCardIdentity.memberName}
          memberCode={cignaCardIdentity.memberCode}
        />
      );
    }
    if (plan?.idCard === IdCardType.EquitableDental || name.includes('equitable')) return <EquitableCard side={currentSide} plan={plan} firstName={applicant?.data?.firstName} lastName={applicant?.data?.lastName} memberId={applicant?.memberId} groupId={applicant?.groupId} />;
    if (plan?.idCard === IdCardType.GuardianVision || name.includes('guardian')) return <GuardianCard firstName={applicant?.data?.firstName} lastName={applicant?.data?.lastName} memberId={applicant?.memberId} groupId={applicant?.groupId} />;
    if (name.includes('legalshield')) return <LegalShieldCard />;
    if (name.includes('telehealth')) return <TelehealthCard />;
    if (category === 'vision') return <VisionCard />;
    if (category === 'dental') return <DentalCard />;
    return <div className="p-6">No ID card available for this plan.</div>;
  };

  const { 
    handleDownload, 
    handleDownloadEquitable, 
    handleDownloadGuardian,
    handleDownloadCigna,
    handlePrint: handlePrintDefault,
    handlePrintEquitable,
    handlePrintGuardian,
    handlePrintCigna,
  } = useIDCardDownload({
    cardRef,
    renderCardForPlan,
    hasBackSide,
  });

  const isEquitable = useMemo(() => {
    const name = (plan?.name || plan?.marketingName || '').toLowerCase();
    return plan?.idCard === IdCardType.EquitableDental || name.includes('equitable');
  }, [plan]);

  const isGuardian = useMemo(() => {
    const name = (plan?.name || plan?.marketingName || '').toLowerCase();
    return plan?.idCard === IdCardType.GuardianVision || name.includes('guardian');
  }, [plan]);

  const isCigna = useMemo(() => {
    const name = (plan?.name || plan?.marketingName || '').toLowerCase();
    return plan?.idCard === IdCardType.CignaMedical || name.includes('cigna');
  }, [plan]);

  const handleDownloadClick = () => {
    if (isEquitable) {
      handleDownloadEquitable();
    } else if (isGuardian) {
      handleDownloadGuardian();
    } else if (isCigna) {
      handleDownloadCigna();
    } else {
      handleDownload();
    }
  };

  const handlePrintClick = () => {
    if (isEquitable) {
      handlePrintEquitable();
    } else if (isGuardian) {
      handlePrintGuardian();
    } else if (isCigna) {
      handlePrintCigna();
    } else {
      handlePrintDefault();
    }
  };

  return (
    <div className="flex flex-col gap-6 mt-5">
      <section className="flex flex-col rounded-3xl bg-white pb-5">
        <div className="bg-primary overflow-hidden relative text-white pl-8 rounded-t-3xl py-7 text-[1.5vw] tracking-wide max-md:text-2xl max-md:py-5">
          <p className="text-3xl 2xl:text-4xl">Your ID Cards</p>
        </div>

        <div className="flex flex-col px-9 max-md:px-5 mt-5 pb-5">
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-black">Your Health Plan Card</h2>
            <p className="text-sm text-gray-600 mt-2 max-w-[660px]">
              Use your digital card when you access care. You can view it, print it, or download and save it to share with your doctor or provider.
            </p>
          </div>

          {hasBackSide && (
            <div className="mb-4 text-sm">
              <span
                className={`cursor-pointer ${cardSide === 'front' ? 'font-semibold text-primary' : 'text-gray-400'}`}
                onClick={() => setCardSide('front')}
              >
                Front of card
              </span>
              <span className="text-gray-400"> | </span>
              <span
                className={`cursor-pointer ${cardSide === 'back' ? 'font-semibold text-primary' : 'text-gray-400'}`}
                onClick={() => setCardSide('back')}
              >
                Back of Card
              </span>
            </div>
          )}

          <div>
            <div ref={cardRef}>
              {renderCardForPlan()}
            </div>
            <div className="flex items-center gap-6 max-xs:gap-3 mt-6">
              <button onClick={handleDownloadClick} className="px-8 max-xs:px-6 py-3 rounded-full bg-primary text-white font-bold">
                DOWNLOAD
              </button>
              <button onClick={handlePrintClick} className="px-8 max-xs:px-6 py-3 rounded-full bg-gray-700 text-white font-bold">
                PRINT
              </button>
              <button onClick={() => navigate(-1)} className="px-8 max-xs:px-6 py-3 rounded-full text-gray-500 font-bold">
                CANCEL
              </button>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};
